import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { droneQueryKeys, fetchDroneCeiling, type DroneLocation } from './drone-api';
import { droneNotice, type DroneAirspaceAnswer, type DroneNotice } from './drone-rules';

export interface UseDroneAirspaceOptions {
    /** The host's whole answer to "may this run": drone products are moving, and the caller can call the route. */
    enabled: boolean;
    location: DroneLocation | null | undefined;
    /** The drone products concerned; none means no request. */
    droneProducts: readonly string[];
    /** The picked dates, `YYYY-MM-DD`. Empty: one undated check, so the restrictions show before a date is picked. */
    shootDates: readonly string[];
}

/**
 * ROBO-854. The airspace notice for drone products at a location: one `checkout/drone-ceiling` per picked date (none picked:
 * one undated), each asked once per page load. Like the weather it never costs the page anything — nothing waits for it, and
 * every failure is a "could not check" line, never a block (ruling 4).
 */
export function useDroneAirspace({
    enabled,
    location,
    droneProducts,
    shootDates
}: UseDroneAirspaceOptions): DroneNotice | null {
    const latitude = location?.latitude;
    const longitude = location?.longitude;
    const hasLocation =
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        // 0,0 is the geocoder's miss marker (ROBO-283), not a place.
        !(latitude === 0 && longitude === 0);
    const isOn = enabled && hasLocation && droneProducts.length > 0;
    const where = { latitude: latitude ?? 0, longitude: longitude ?? 0 };
    const dates: (string | null)[] = shootDates.length > 0 ? [...new Set(shootDates)] : [null];

    const results = useQueries({
        queries: dates.map(shootDate => ({
            queryKey: droneQueryKeys.airspace(where, shootDate),
            queryFn: async () => {
                try {
                    return await fetchDroneCeiling(where, shootDate);
                } catch (error) {
                    console.warn(
                        'Drone airspace unavailable; the page says it could not check.',
                        error
                    );
                    throw error;
                }
            },
            enabled: isOn,
            staleTime: Infinity,
            gcTime: Infinity,
            retry: false,
            retryOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            throwOnError: false
        }))
    });

    const answers: DroneAirspaceAnswer[] = isOn
        ? results.flatMap((result, index): DroneAirspaceAnswer[] =>
              result.data
                  ? [{ shootDate: dates[index], ceiling: result.data }]
                  : result.isError
                    ? [{ shootDate: dates[index], failed: true }]
                    : []
          )
        : [];
    const answerKey = JSON.stringify(answers);
    const productKey = droneProducts.join('\n');

    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialised answers, not the fresh array
    return useMemo(() => droneNotice(droneProducts, answers), [answerKey, productKey]);
}
